import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from '../../hooks/useTranslation';

const { FiX, FiMail, FiLock } = FiIcons;

export default function AuthModal({ isOpen, onClose }) {
  const { signIn, signUp } = useAuth();
  const { contentLanguage } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setError(null);
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isLogin, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isLogin) {
        const { error } = await signIn(email, password);
        if (error) throw error;
      } else {
        const { error } = await signUp(email, password, firstName, lastName);
        if (error) throw error;
      }
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[99999] flex flex-col items-center justify-center">
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-900/90 backdrop-blur-md"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-[calc(100%-2rem)] max-w-md bg-white rounded-[2.5rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.5)] overflow-hidden z-[100000]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-slate-900 p-10 text-white relative">
              <button 
                onClick={onClose} 
                className="absolute top-6 right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>

              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-blue-500/30">
                <span className="text-white font-black text-2xl">H</span>
              </div>
              
              <h2 className="text-3xl font-black leading-tight tracking-tight">
                {isLogin 
                  ? (contentLanguage === 'el' ? 'Καλώς ήρθατε' : 'Welcome') 
                  : (contentLanguage === 'el' ? 'Εγγραφή' : 'Join Us')}
              </h2>
              <p className="text-slate-400 mt-2 text-sm font-medium">
                {isLogin 
                  ? (contentLanguage === 'el' ? 'Συνδεθείτε στον λογαριασμό σας' : 'Sign in to your account') 
                  : (contentLanguage === 'el' ? 'Δημιουργήστε έναν νέο λογαριασμό' : 'Create a new account')}
              </p>
            </div>

            {/* Form */}
            <div className="p-10">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-xl text-sm font-bold">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {!isLogin && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">
                        {contentLanguage === 'el' ? 'Όνομα' : 'First Name'}
                      </label>
                      <input 
                        type="text" required value={firstName} onChange={e => setFirstName(e.target.value)}
                        className="w-full px-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all outline-none text-slate-900 font-bold"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">
                        {contentLanguage === 'el' ? 'Επίθετο' : 'Last Name'}
                      </label>
                      <input 
                        type="text" required value={lastName} onChange={e => setLastName(e.target.value)}
                        className="w-full px-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all outline-none text-slate-900 font-bold"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Email</label>
                  <div className="relative">
                    <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400">
                      <SafeIcon icon={FiMail} />
                    </div>
                    <input 
                      type="email" required value={email} onChange={e => setEmail(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all outline-none text-slate-900 font-bold"
                      placeholder="name@email.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">
                    {contentLanguage === 'el' ? 'Κωδικός' : 'Password'}
                  </label>
                  <div className="relative">
                    <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400">
                      <SafeIcon icon={FiLock} />
                    </div>
                    <input 
                      type="password" required value={password} onChange={e => setPassword(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all outline-none text-slate-900 font-bold"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <button 
                  type="submit" disabled={loading}
                  className="w-full py-5 bg-blue-600 text-white font-black rounded-2xl hover:bg-blue-700 shadow-xl shadow-blue-500/30 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <span>{isLogin ? (contentLanguage === 'el' ? 'Σύνδεση' : 'Sign In') : (contentLanguage === 'el' ? 'Δημιουργία' : 'Create Account')}</span>
                  )}
                </button>
              </form>

              <div className="mt-8 pt-8 border-t border-slate-100 flex justify-center">
                <button 
                  type="button" onClick={() => setIsLogin(!isLogin)}
                  className="text-sm font-bold text-slate-500 hover:text-blue-600 transition-colors"
                >
                  {isLogin 
                    ? (contentLanguage === 'el' ? 'Δεν έχετε λογαριασμό; Εγγραφείτε' : "Don't have an account? Sign up") 
                    : (contentLanguage === 'el' ? 'Έχετε ήδη λογαριασμό; Συνδεθείτε' : "Already have an account? Sign in")}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
}